import FilterPanelCheckboxGroupItem from 'components/Common/FilterPanel/FilterPanelCheckboxGroupItem'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import ToggleButton from 'LuxKit/ToggleButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import CONTINENT_AIRPORT_INDEX from './loungeData/continentAirportIndex'
import AIRPORT_LOUNGE_INDEX from './loungeData/airportLoungeIndex'
import type { LoungeRegion } from './loungePassesConstants'

const DropdownContent = styled.div`
  width: ${rem(300)};
  min-width: ${rem(220)};
  max-width: ${rem(350)};
  padding: 0 ${rem(20)};
`

interface FilterContentProps {
  region: LoungeRegion
  items: Array<FilterPanelCheckItem>
  allSelected: boolean
  allRegionName: string
  totalCountLabel: string
  onCheckAll: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function LoungeRegionFilterContent({ region, items, allSelected, allRegionName, totalCountLabel, onCheckAll, onChange }: FilterContentProps) {
  return <VerticalSpacer gap={16}>
    <CheckboxInput
      name="all"
      value="all"
      size="medium"
      checked={allSelected}
      onChange={onCheckAll}
    >
      <Group direction="horizontal" horizontalAlign="space-between">
        {allRegionName}
        <span>{totalCountLabel}</span>
      </Group>
    </CheckboxInput>
    {items.map(item =>
      <FilterPanelCheckboxGroupItem
        key={item.value}
        item={item}
        name={region}
        onChange={onChange}
      />,
    )}
  </VerticalSpacer>
}

interface FilterPanelProps {
  region: LoungeRegion
  regionName: string
  selectedCountries: Set<string>
}

function LoungeRegionFilterPanel({ region, regionName, selectedCountries }: FilterPanelProps) {
  const { resolve } = useModalElementContext<Set<string> | undefined>()
  const intl = useIntl()
  const [localSelected, setLocalSelected] = useState(selectedCountries)

  const countries = CONTINENT_AIRPORT_INDEX[region]

  const items: Array<FilterPanelCheckItem> = useMemo(() => {
    return countries.map(c => {
      const loungeCount = c.airports.reduce((sum, airport) => {
        return sum + (AIRPORT_LOUNGE_INDEX[airport.id]?.length ?? 0)
      }, 0)
      return {
        label: c.label,
        value: c.key,
        checked: localSelected.has(c.key),
        count: loungeCount,
      }
    })
  }, [countries, localSelected])

  const totalLoungeCount = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.count ?? 0), 0)
  }, [items])

  const allSelected = items.length > 0 && items.every(item => localSelected.has(String(item.value)))

  const handleCheckAll = useCallback(() => {
    setLocalSelected(allSelected ? new Set() : new Set(countries.map(c => c.key)))
  }, [allSelected, countries])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSelected(prev => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const resetText = intl.formatMessage({
    id: '7rNyPf1KVf8rgXAtbZRm',
    defaultMessage: 'Reset',
    description: 'Reset button label in the lounge region country filter dropdown',
  })

  const applyText = intl.formatMessage({
    id: '3vCd-EleqPkpwZR00oNo',
    defaultMessage: 'Apply',
    description: 'Apply button label in the lounge region country filter dropdown',
  })

  const allRegionName = intl.formatMessage(
    {
      id: 'zzCHh9oMCKsgzbxRgbAP',
      defaultMessage: 'All {regionName}',
      description: 'Checkbox label to select all countries in a lounge region filter',
    },
    { regionName },
  )

  const totalCountLabel = intl.formatMessage(
    {
      id: 'AQj1DTTPdmt-bGxPXG7x',
      defaultMessage: '({count})',
      description: 'Parenthesised count of matching results shown next to a filter checkbox option',
    },
    { count: totalLoungeCount },
  )

  return <ModalBase>
    <ModalHeader
      title={regionName}
      onCloseButtonClick={_e => resolve(undefined)}
    />
    <ModalBody>
      <ModalContent>
        <LoungeRegionFilterContent
          region={region}
          items={items}
          allSelected={allSelected}
          allRegionName={allRegionName}
          totalCountLabel={totalCountLabel}
          onCheckAll={handleCheckAll}
          onChange={handleChange}
        />
      </ModalContent>
    </ModalBody>
    <ModalFooter
      secondaryActionProps={{
        children: resetText,
        onClick: () => setLocalSelected(new Set()),
      }}
      primaryActionProps={{
        children: applyText,
        onClick: () => resolve(localSelected),
      }}
    />
  </ModalBase>
}

interface Props {
  region: LoungeRegion
  regionName: string
  isSelected: boolean
  selectedCountries: Set<string>
  onApply: (region: LoungeRegion, selectedCountries: Set<string>) => void
}

function LoungePassesRegionFilterDropdown({ region, regionName, isSelected, selectedCountries, onApply }: Props) {
  const isDesktop = useScreenSizeUp('tablet')
  const showModal = useContext(ModalContext)
  const intl = useIntl()
  const [isOpen, toggle, , close] = useToggle()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [localSelected, setLocalSelected] = useState(selectedCountries)

  // Sync local state from props when opening
  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedCountries)
    }
  }, [isOpen, selectedCountries])

  const countries = CONTINENT_AIRPORT_INDEX[region]

  const items: Array<FilterPanelCheckItem> = useMemo(() => {
    return countries.map(c => {
      const loungeCount = c.airports.reduce((sum, airport) => {
        return sum + (AIRPORT_LOUNGE_INDEX[airport.id]?.length ?? 0)
      }, 0)
      return {
        label: c.label,
        value: c.key,
        checked: localSelected.has(c.key),
        count: loungeCount,
      }
    })
  }, [countries, localSelected])

  const totalLoungeCount = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.count ?? 0), 0)
  }, [items])

  const allSelected = items.length > 0 && items.every(item => localSelected.has(String(item.value)))

  const handleCheckAll = useCallback(() => {
    setLocalSelected(allSelected ? new Set() : new Set(countries.map(c => c.key)))
  }, [allSelected, countries])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSelected(prev => {
      const next = new Set(prev)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply(region, localSelected)
    close()
  }, [region, localSelected, onApply, close])

  const handleClick = useCallback(async() => {
    if (isDesktop) {
      toggle()
    } else {
      const result = await showModal<Set<string> | undefined>(
        <LoungeRegionFilterPanel
          region={region}
          regionName={regionName}
          selectedCountries={selectedCountries}
        />,
      )
      if (result !== undefined) {
        onApply(region, result)
      }
    }
  }, [isDesktop, toggle, showModal, region, regionName, selectedCountries, onApply])

  const resetText = intl.formatMessage({
    id: '7rNyPf1KVf8rgXAtbZRm',
    defaultMessage: 'Reset',
    description: 'Reset button label in the lounge region country filter dropdown',
  })

  const applyText = intl.formatMessage({
    id: '3vCd-EleqPkpwZR00oNo',
    defaultMessage: 'Apply',
    description: 'Apply button label in the lounge region country filter dropdown',
  })

  const allRegionName = intl.formatMessage(
    {
      id: 'zzCHh9oMCKsgzbxRgbAP',
      defaultMessage: 'All {regionName}',
      description: 'Checkbox label to select all countries in a lounge region filter',
    },
    { regionName },
  )

  const totalCountLabel = intl.formatMessage(
    {
      id: 'AQj1DTTPdmt-bGxPXG7x',
      defaultMessage: '({count})',
      description: 'Parenthesised count of matching results shown next to a filter checkbox option',
    },
    { count: totalLoungeCount },
  )

  return <>
    <ToggleButton
      ref={buttonRef}
      kind="secondary"
      orientation="horizontal"
      isSelected={isSelected}
      title={regionName}
      endIcon={isOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      onClick={handleClick}
    />
    <DropdownList
      triggerRef={buttonRef}
      anchorRef={buttonRef}
      open={isOpen}
      onClose={() => close()}
      title={regionName}
      hasDismissButton
      portaled
      autoHeight
      size="S"
      secondaryActionProps={{ kind: 'tertiary', children: resetText, onClick: () => setLocalSelected(new Set()) }}
      primaryActionProps={{ children: applyText, onClick: handleApply }}
    >
      <DropdownContent>
        <LoungeRegionFilterContent
          region={region}
          items={items}
          allSelected={allSelected}
          allRegionName={allRegionName}
          totalCountLabel={totalCountLabel}
          onCheckAll={handleCheckAll}
          onChange={handleChange}
        />
      </DropdownContent>
    </DropdownList>
  </>
}

export default LoungePassesRegionFilterDropdown
